import { Button, Table, Tag, Modal, message, Form, Input, Select } from "antd";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import axios from "axios";
import moment from "moment";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { serverUrl } from "../../../../../nestserver";
import { selectUser } from "../../../../../redux/slices/auth.slice";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const PermissionHistory = ({ setStep }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useSelector(selectUser);
  const [withdrawModalVisible, setWithdrawModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Fetch students
  async function fetchStudent() {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/students/by-parent/`,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data.result;
  }
  const students = useQuery("students/by-parent", fetchStudent);

  const handleSelectChange = (studentId) => {
    const student =
      students &&
      students?.data &&
      students?.data.length > 0 &&
      students?.data?.find((data) => data?._id === studentId);
    if (student) {
      setSelectedStudent(student?.class?.name);
    }
  };

  const fetchPermissions = async () => {
    const res = await axios.get(`${serverUrl}/student-permission`, {
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };

  const permissionList = useQuery(["student-permissions"], fetchPermissions);

  const handleWithdraw = async () => {
    try {
      const res = await axios.patch(
        `${serverUrl}/student-permission/${selectedRecord._id}/withdraw`,
        {},
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      message.success(
        res?.data?.result?.message || "Permission withdrawn successfully",
      );
      queryClient.invalidateQueries(["student-permissions"]);
      setWithdrawModalVisible(false);
    } catch (err) {
      message.error(
        err?.response?.data?.error?.errors || "Failed to withdraw permission",
      );
    }
  };

  const handleCreatePermission = async (values) => {
    setLoading(true);
    try {
      const payload = {
        student: values.name,
        type: values.type,
        from: values.dateRange[0].format("YYYY-MM-DD"),
        to: values.dateRange[1].format("YYYY-MM-DD"),
        title: values.title,
        description: values.description,
      };

      const res = await axios.post(`${serverUrl}/student-permission`, payload, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });

      message.success(
        res?.data?.result?.message || "Permission request created successfully",
      );
      queryClient.invalidateQueries(["student-permissions"]);
      form.resetFields();
      setSelectedStudent(null);
      setCreateModalVisible(false);
    } catch (err) {
      message.error(
        err?.response?.data?.error?.errors ||
          "Failed to create permission request",
      );
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Date Requested",
      render: (record) => (
        <div>{moment(record?.createdAt).format("DD-MM-YYYY")}</div>
      ),
    },
    {
      title: "Student",
      render: (record) => <div>{record?.student}</div>,
    },
    {
      title: "Type",
      render: (record) => <Tag color="blue">{record?.type}</Tag>,
    },
    {
      title: "Date Range",
      render: (record) => {
        const fromDate = moment(record?.from).format("DD-MM-YYYY");
        const toDate = moment(record?.to).format("DD-MM-YYYY");
        return (
          <div>
            {fromDate === toDate ? fromDate : `${fromDate} to ${toDate}`}
          </div>
        );
      },
    },
    {
      title: "Title",
      render: (record) => <div>{record?.title}</div>,
    },
    {
      title: "Status",
      render: (record) => {
        const color =
          record?.status === "approved"
            ? "green"
            : record?.status === "rejected"
              ? "red"
              : record?.status === "withdraw"
                ? "gray"
                : "orange";
        return <Tag color={color}>{record?.status?.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Action",
      render: (record) => (
        <Button
          size="small"
          danger
          onClick={() => {
            setSelectedRecord(record);
            setWithdrawModalVisible(true);
          }}
          disabled={record?.status !== "pending"}
        >
          Withdraw
        </Button>
      ),
    },
  ];

  return (
    <div className="pt-8">
      <div className="bg-white p-10 rounded-2xl">
        <div className="flex justify-between items-center pb-8">
          <div className="text-2xl text-orange-500 font-semibold">
            Permission Requests
          </div>
          <Button type="primary" onClick={() => setCreateModalVisible(true)}>
            Create New Request
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={
            permissionList?.data?.result?.data
              ? permissionList?.data?.result?.data?.sort((a, b) =>
                  moment(a?.createdAt).isBefore(b?.createdAt) ? 1 : -1,
                )
              : []
          }
          loading={permissionList?.isLoading}
          rowKey={(record) => record._id}
          pagination={{ pageSize: 10 }}
        />
      </div>

      {/* Withdraw Modal */}
      <Modal
        title="Withdraw Permission Request"
        visible={withdrawModalVisible}
        onCancel={() => setWithdrawModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setWithdrawModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="withdraw" type="primary" danger onClick={handleWithdraw}>
            Withdraw
          </Button>,
        ]}
      >
        <p>Are you sure you want to withdraw this permission request?</p>
        {selectedRecord && (
          <div className="mt-4">
            <p>
              <strong>Type:</strong> {selectedRecord.type}
            </p>
            <p>
              <strong>Date Range:</strong>{" "}
              {(() => {
                const fromDate = moment(selectedRecord.from).format(
                  "DD-MM-YYYY",
                );
                const toDate = moment(selectedRecord.to).format("DD-MM-YYYY");
                return fromDate === toDate
                  ? fromDate
                  : `${fromDate} to ${toDate}`;
              })()}
            </p>
          </div>
        )}
      </Modal>

      {/* Create Permission Modal */}
      <Modal
        title="Create Permission Request"
        visible={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
          setSelectedStudent(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreatePermission}
          autoComplete="off"
        >
          <Form.Item
            label="Name"
            name="name"
            className="w-full"
            rules={[
              {
                required: true,
                message: "Please select name",
              },
            ]}
          >
            <Select
              showSearch
              showArrow
              placeholder="Select name"
              className="w-full bg-gray-300"
              style={{
                border: "6px",
                backgroundColor: "#EBEBEB",
              }}
              getPopupContainer={(trigger) => trigger.parentNode}
              onChange={(value) => handleSelectChange(value)}
            >
              {students &&
                students?.data?.map((data) => {
                  return (
                    <Option key={data._id} value={data._id}>
                      {`${data?.first_name} ${data?.last_name}`}
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>
          <Form.Item label="Class" className="w-full">
            <Input readonly value={selectedStudent} />
          </Form.Item>

          <Form.Item
            label="Leave Type"
            name="type"
            rules={[{ required: true, message: "Please select leave type" }]}
          >
            <Select placeholder="Select leave type">
              <Option value="Sick Leave">Sick Leave</Option>
              <Option value="Casual Leave">Casual Leave</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Date Range"
            name="dateRange"
            rules={[{ required: true, message: "Please select date range" }]}
          >
            <RangePicker
              format="DD-MM-YYYY"
              disabledDate={(d) => !d || d.isBefore(moment().startOf("day"))}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            label="Title"
            name="title"
            rules={[
              { required: true, message: "Please enter title" },
              {
                min: 1,
                max: 15,
                message: "Title must be between 1 and 15 characters",
              },
            ]}
          >
            <Input placeholder="Brief reason" maxLength={15} />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[
              {
                validator: (_, value) => {
                  if (value && value.length <= 150) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    "Description must be at most 150 characters",
                  );
                },
              },
            ]}
          >
            <TextArea rows={4} placeholder="Reason" maxLength={150} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Submit Request
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => {
                setCreateModalVisible(false);
                form.resetFields();
                setSelectedStudent(null);
              }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PermissionHistory;
